import React, { useState, useCallback } from 'react';
import ModernAlert, { AlertType } from '../components/Common/ModernAlert';
import czechTranslations from '../translations/czech';

interface AlertConfig {
  title: string;
  message: string;
  type?: AlertType;
  confirmText?: string;
  showCancel?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface UseModernAlertResult {
  showAlert: (config: AlertConfig) => void;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
  showConfirm: (config: Omit<AlertConfig, 'showCancel'>) => void;
  AlertComponent: React.ReactNode;
}

export const useModernAlert = (): UseModernAlertResult => {
  const [open, setOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState<AlertConfig>({
    title: '',
    message: '',
    type: 'info',
  });

  const showAlert = useCallback((config: AlertConfig) => {
    setAlertConfig(config);
    setOpen(true);
  }, []);

  const showSuccess = useCallback((message: string, title?: string) => {
    showAlert({
      title: title || '✅ Úspěch',
      message,
      type: 'success',
    });
  }, [showAlert]);

  const showError = useCallback((message: string, title?: string) => {
    showAlert({
      title: title || '❌ Chyba',
      message,
      type: 'error',
    });
  }, [showAlert]);

  const showWarning = useCallback((message: string, title?: string) => {
    showAlert({
      title: title || '⚠️ Upozornění',
      message,
      type: 'warning',
    });
  }, [showAlert]);

  const showInfo = useCallback((message: string, title?: string) => {
    showAlert({
      title: title || 'ℹ️ Informace',
      message,
      type: 'info',
    });
  }, [showAlert]);

  const showConfirm = useCallback((config: Omit<AlertConfig, 'showCancel'>) => {
    showAlert({
      ...config,
      showCancel: true,
      confirmText: config.confirmText || 'Potvrdit',
    });
  }, [showAlert]);

  const handleClose = () => {
    setOpen(false);
  };

  const AlertComponent = (
    <ModernAlert
      open={open}
      onClose={handleClose}
      title={alertConfig.title}
      message={alertConfig.message}
      type={alertConfig.type}
      confirmText={alertConfig.confirmText}
      showCancel={alertConfig.showCancel}
      onConfirm={alertConfig.onConfirm}
      onCancel={alertConfig.onCancel}
    />
  );

  return {
    showAlert,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm,
    AlertComponent,
  };
};

export default useModernAlert;

